import { z } from "zod";

// ---------------------------------------------------------------------------
// Custom Event Type Registry Service (C.3)
// ---------------------------------------------------------------------------
// Allows tenants to define their own event types and fire them.
// This is the service layer — HTTP endpoints are in C.7, DB schema in A.4.
//
// All external calls are dependency-injected for testability.
// Tenant isolation enforced via tenantId on every operation.
// ---------------------------------------------------------------------------

// ---------------------------------------------------------------------------
// Constants
// ---------------------------------------------------------------------------

/** All custom event types must match this format: custom.lowercase_name */
const EVENT_TYPE_PATTERN = /^custom\.[a-z][a-z0-9_]{1,98}$/;

/** Maximum custom event types per tenant (soft limit, configurable) */
const DEFAULT_MAX_TYPES_PER_TENANT = 50;

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

export interface CustomEventType {
  id: string;
  tenantId: string;
  eventType: string;
  displayName: string;
  description: string | null;
  payloadSchema: Record<string, unknown> | null;
  createdAt: Date;
  updatedAt: Date;
  deletedAt: Date | null;
}

export type ActorType = "user" | "system" | "automation" | "api";

// ---------------------------------------------------------------------------
// Input schemas
// ---------------------------------------------------------------------------

export const registerEventTypeInputSchema = z.object({
  eventType: z.string()
    .min(8) // "custom.x" minimum
    .max(100)
    .regex(EVENT_TYPE_PATTERN, "Must match format: custom.<lowercase_name> (letters, numbers, underscores only)"),
  displayName: z.string().min(1).max(100),
  description: z.string().max(500).optional(),
  payloadSchema: z.record(z.unknown()).optional(), // JSON Schema (validated at API layer)
}).strict();

export type RegisterEventTypeInput = z.infer<typeof registerEventTypeInputSchema>;

export const fireEventInputSchema = z.object({
  eventType: z.string().min(1),
  payload: z.record(z.unknown()).default({}),
  actorType: z.enum(["user", "system", "automation", "api"]).default("api"),
  entityId: z.string().optional(), // Optional entity reference
  correlationId: z.string().uuid().optional(),
}).strict();

export type FireEventInput = z.infer<typeof fireEventInputSchema>;

// ---------------------------------------------------------------------------
// Service result pattern
// ---------------------------------------------------------------------------

export type ServiceResult<T> =
  | { data: T; error?: undefined }
  | { error: string; message: string; data?: undefined };

// ---------------------------------------------------------------------------
// Dependencies (all injected — no direct DB or queue imports)
// ---------------------------------------------------------------------------

export interface CustomEventServiceDeps {
  /** Find a custom event type by tenant + eventType name */
  findByType: (tenantId: string, eventType: string) => Promise<CustomEventType | null>;
  /** List all active custom event types for a tenant */
  findByTenant: (tenantId: string) => Promise<CustomEventType[]>;
  /** Count active custom event types for a tenant */
  count: (tenantId: string) => Promise<number>;
  /** Insert a new custom event type */
  insert: (record: Omit<CustomEventType, "id" | "createdAt" | "updatedAt" | "deletedAt">) => Promise<CustomEventType>;
  /** Soft-delete a custom event type (set deletedAt) */
  softDelete: (tenantId: string, eventType: string) => Promise<boolean>;
  /** Validate payload against a JSON Schema (injected — no ajv dependency here) */
  validatePayload?: (schema: Record<string, unknown>, payload: unknown) => { valid: boolean; errors?: string[] };
  /** Emit the custom event into the CQRS event pipeline */
  emitEvent: (event: {
    tenantId: string;
    eventType: string;
    entityType: string;
    entityId: string;
    payload: Record<string, unknown>;
    actorType: ActorType;
    correlationId?: string;
  }) => Promise<{ eventId: string }>;
  /** Logger */
  logger?: { warn: (msg: string, meta?: Record<string, unknown>) => void };
  /** Max types per tenant (overridable for testing) */
  maxTypesPerTenant?: number;
}

// ---------------------------------------------------------------------------
// Service factory
// ---------------------------------------------------------------------------

export interface CustomEventService {
  registerEventType(tenantId: string, input: RegisterEventTypeInput): Promise<ServiceResult<CustomEventType>>;
  getEventType(tenantId: string, eventType: string): Promise<CustomEventType | null>;
  listEventTypes(tenantId: string): Promise<CustomEventType[]>;
  fireEvent(tenantId: string, input: FireEventInput): Promise<ServiceResult<{ eventId: string }>>;
  deleteEventType(tenantId: string, eventType: string): Promise<ServiceResult<void>>;
}

export function createCustomEventService(deps: CustomEventServiceDeps): CustomEventService {
  const maxTypes = deps.maxTypesPerTenant ?? DEFAULT_MAX_TYPES_PER_TENANT;

  return {
    async registerEventType(tenantId, input) {
      // Validate input
      const parsed = registerEventTypeInputSchema.safeParse(input);
      if (!parsed.success) {
        const firstIssue = parsed.error.issues[0];
        return { error: "validation_error", message: firstIssue?.message ?? "Invalid event type input" };
      }

      // Check for duplicate
      const existing = await deps.findByType(tenantId, parsed.data.eventType);
      if (existing) {
        return { error: "duplicate", message: `Event type "${parsed.data.eventType}" already exists` };
      }

      // Check tenant limit
      const currentCount = await deps.count(tenantId);
      if (currentCount >= maxTypes) {
        return { error: "limit_exceeded", message: `Maximum ${maxTypes} custom event types per tenant` };
      }

      // Insert
      const record = await deps.insert({
        tenantId,
        eventType: parsed.data.eventType,
        displayName: parsed.data.displayName,
        description: parsed.data.description ?? null,
        payloadSchema: parsed.data.payloadSchema ?? null,
      });

      return { data: record };
    },

    async getEventType(tenantId, eventType) {
      return deps.findByType(tenantId, eventType);
    },

    async listEventTypes(tenantId) {
      return deps.findByTenant(tenantId);
    },

    async fireEvent(tenantId, input) {
      // Validate input shape
      const parsed = fireEventInputSchema.safeParse(input);
      if (!parsed.success) {
        const firstIssue = parsed.error.issues[0];
        return { error: "validation_error", message: firstIssue?.message ?? "Invalid event payload" };
      }

      // Verify event type is registered for this tenant
      const eventType = await deps.findByType(tenantId, parsed.data.eventType);
      if (!eventType) {
        return { error: "unregistered_type", message: `Event type "${parsed.data.eventType}" is not registered` };
      }

      // Validate payload against schema (if schema defined and validator available)
      if (eventType.payloadSchema && deps.validatePayload) {
        const validation = deps.validatePayload(eventType.payloadSchema, parsed.data.payload);
        if (!validation.valid) {
          return {
            error: "payload_invalid",
            message: `Payload validation failed: ${validation.errors?.join(", ") ?? "unknown error"}`,
          };
        }
      }

      // Emit into CQRS event pipeline
      const result = await deps.emitEvent({
        tenantId,
        eventType: parsed.data.eventType,
        entityType: "custom_event",
        entityId: parsed.data.entityId ?? tenantId, // Default entity is the tenant itself
        payload: parsed.data.payload,
        actorType: parsed.data.actorType,
        correlationId: parsed.data.correlationId,
      });

      return { data: { eventId: result.eventId } };
    },

    async deleteEventType(tenantId, eventType) {
      const deleted = await deps.softDelete(tenantId, eventType);
      if (!deleted) {
        return { error: "not_found", message: `Event type "${eventType}" not found` };
      }
      return { data: undefined };
    },
  };
}
